@if (isOpen) {
  <div class="msg-overlay" (click)="close()"></div>
  <aside
    class="msg-drawer"
    role="dialog"
    [attr.aria-label]="'messages' | translate"
    (keydown.escape)="onEscape($event)"
  >
    <header class="msg-drawer__head">
      <div class="msg-drawer__title">
        @if (view !== 'list') {
          <button
            type="button"
            class="msg-drawer__icon-btn"
            (click)="back()"
            [title]="'back' | translate"
            [attr.aria-label]="'back' | translate"
          >
            <kendo-icon name="arrow-chevron-left"></kendo-icon>
          </button>
        }
        <span class="msg-drawer__title-text">
          @switch (view) {
            @case ('thread') { {{ thread?.title }} }
            @case ('new') { {{ 'newMessage' | translate }} }
            @default { {{ 'messages' | translate }} }
          }
        </span>
      </div>
      <div class="msg-drawer__head-actions">
        @if (view === 'thread' && recordUrl) {
          <button
            type="button"
            class="msg-drawer__icon-btn"
            (click)="openRecord()"
            [title]="'openRecord' | translate"
            [attr.aria-label]="'openRecord' | translate"
          >
            <kendo-icon name="hyperlink-open"></kendo-icon>
          </button>
        }
        @if (view === 'thread' && thread?.kind === 'Group' && thread?.isMember) {
          <button
            type="button"
            class="msg-drawer__icon-btn"
            (click)="leaveGroup()"
            [title]="'leaveGroup' | translate"
            [attr.aria-label]="'leaveGroup' | translate"
          >
            <kendo-icon name="logout"></kendo-icon>
          </button>
        }
        @if (view === 'list') {
          <button
            type="button"
            class="msg-drawer__icon-btn"
            (click)="newMessage()"
            [title]="'newMessage' | translate"
            [attr.aria-label]="'newMessage' | translate"
          >
            <kendo-icon name="plus"></kendo-icon>
          </button>
        }
        <button
          type="button"
          class="msg-drawer__icon-btn"
          (click)="close()"
          [title]="'close' | translate"
          [attr.aria-label]="'close' | translate"
        >
          <kendo-icon name="close"></kendo-icon>
        </button>
      </div>
    </header>

    @if (view === 'thread' && thread?.kind !== 'Direct' && thread?.members?.length) {
      <div class="msg-drawer__members">
        @for (member of thread!.members; track member.uid; let last = $last) {
          <span>{{ member.name }}{{ last ? '' : ',' }}</span>
        }
      </div>
    }

    @if (error) {
      <div class="msg-drawer__error" role="alert">{{ error }}</div>
    }

    <!-- The conversation list -->
    @if (view === 'list') {
      <div class="msg-drawer__search">
        <input
          type="search"
          class="k-textbox k-input k-input-md k-rounded-md"
          [placeholder]="'search' | translate"
          [(ngModel)]="filter"
        />
      </div>
      <div class="msg-drawer__body">
        @if (!filteredConversations.length) {
          <p class="msg-drawer__empty">{{ 'noConversationsYet' | translate }}</p>
        }
        <ul class="msg-list">
          @for (c of filteredConversations; track c.uid) {
            <li>
              <button type="button" class="msg-list__item" (click)="openThread(c.uid)">
                <span class="msg-list__icon" [class.is-doc]="c.kind === 'Document'">
                  <kendo-icon [name]="c.kind === 'Document' ? 'file-txt' : c.kind === 'Group' ? 'user' : 'comment'"></kendo-icon>
                </span>
                <span class="msg-list__main">
                  <span class="msg-list__row">
                    <span class="msg-list__title" [class.is-unread]="c.unreadCount > 0">{{ c.title }}</span>
                    <span class="msg-list__time">{{ timeLabel(c.lastMessageAt) }}</span>
                  </span>
                  <span class="msg-list__row">
                    <span class="msg-list__preview">
                      @if (c.lastMessageSenderName && c.kind !== 'Direct') {
                        <b>{{ c.lastMessageSenderName }}:</b>
                      }
                      {{ c.lastMessagePreview }}
                    </span>
                    @if (c.unreadCount > 0) {
                      <span class="msg-badge">{{ c.unreadCount }}</span>
                    }
                  </span>
                </span>
              </button>
            </li>
          }
        </ul>
      </div>
    }

    <!-- One conversation -->
    @if (view === 'thread') {
      <div class="msg-drawer__body msg-thread" #scroller (scroll)="onThreadScroll()">
        @if (loadingThread) {
          <div class="msg-drawer__loading">
            <kendo-loader type="infinite-spinner" size="medium"></kendo-loader>
          </div>
        } @else {
          @if (hasMore) {
            <button type="button" class="msg-thread__older" [disabled]="loadingOlder" (click)="loadOlder()">
              {{ 'loadOlderMessages' | translate }}
            </button>
          }
          @for (m of messages; track m.uid) {
            <div class="msg" [class.is-mine]="m.isMine">
              @if (!m.isMine) {
                <div class="msg__sender">{{ m.senderName }}</div>
              }
              @if (editingUid === m.uid) {
                <div class="msg__edit">
                  <textarea
                    class="k-textarea k-input k-rounded-md"
                    rows="3"
                    [(ngModel)]="editDraft"
                    (keydown.escape)="$event.stopPropagation(); cancelEdit()"
                  ></textarea>
                  <div class="msg__edit-actions">
                    <button kendoButton size="small" fillMode="flat" (click)="cancelEdit()">{{ 'cancel' | translate }}</button>
                    <button kendoButton size="small" themeColor="primary" (click)="saveEdit(m)">{{ 'save' | translate }}</button>
                  </div>
                </div>
              } @else {
                <div class="msg__bubble" [class.is-deleted]="m.isDeleted">
                  @if (m.isDeleted) {
                    <em>{{ 'messageDeleted' | translate }}</em>
                  } @else {
                    {{ m.body }}
                  }
                </div>
                <div class="msg__meta">
                  <span>{{ timeLabel(m.sentAt) }}</span>
                  @if (m.editedAt && !m.isDeleted) {
                    <span>· {{ 'edited' | translate }}</span>
                  }
                  @if (m.isMine && !m.isDeleted) {
                    @if (confirmingDeleteUid === m.uid) {
                      <span class="msg__confirm">
                        {{ 'deleteMessageQuestion' | translate }}
                        <button type="button" class="msg__link" (click)="confirmDelete(m)">{{ 'yes' | translate }}</button>
                        <button type="button" class="msg__link" (click)="confirmingDeleteUid = null">{{ 'no' | translate }}</button>
                      </span>
                    } @else {
                      <button type="button" class="msg__link" (click)="startEdit(m)">{{ 'edit' | translate }}</button>
                      <button type="button" class="msg__link" (click)="askDelete(m)">{{ 'delete' | translate }}</button>
                    }
                  }
                </div>
              }
            </div>
          }
        }
      </div>
      <div class="msg-composer">
        <textarea
          class="k-textarea k-input k-rounded-md"
          rows="2"
          maxlength="4000"
          [placeholder]="'writeAMessage' | translate"
          [(ngModel)]="draft"
          (keydown)="onComposerKeydown($event)"
        ></textarea>
        <button
          type="button"
          class="msg-composer__send"
          [disabled]="!draft.trim() || sending"
          (click)="send()"
          [title]="'send' | translate"
          [attr.aria-label]="'send' | translate"
        >
          <kendo-icon name="paper-plane"></kendo-icon>
        </button>
      </div>
    }

    <!-- A new conversation -->
    @if (view === 'new') {
      <div class="msg-drawer__search">
        <input
          type="search"
          class="k-textbox k-input k-input-md k-rounded-md"
          [placeholder]="'searchPeople' | translate"
          [ngModel]="peopleSearch"
          (ngModelChange)="onPeopleSearch($event)"
        />
      </div>
      @if (picked.length) {
        <div class="msg-picked">
          @for (p of picked; track p.uid) {
            <button type="button" class="msg-chip" (click)="togglePick(p)">
              {{ p.name }} <kendo-icon name="close"></kendo-icon>
            </button>
          }
        </div>
      }
      @if (picked.length > 1) {
        <div class="msg-drawer__search">
          <input
            type="text"
            class="k-textbox k-input k-input-md k-rounded-md"
            maxlength="200"
            [placeholder]="'groupName' | translate"
            [(ngModel)]="groupName"
          />
        </div>
      }
      <div class="msg-drawer__body">
        <ul class="msg-list">
          @for (p of people; track p.uid) {
            <li>
              <button type="button" class="msg-list__item" [class.is-picked]="isPicked(p)" (click)="togglePick(p)">
                <span class="msg-list__icon">
                  <kendo-icon [name]="isPicked(p) ? 'check' : 'user'"></kendo-icon>
                </span>
                <span class="msg-list__main">
                  <span class="msg-list__title">{{ p.name }}</span>
                  @if (p.email) {
                    <span class="msg-list__preview">{{ p.email }}</span>
                  }
                </span>
              </button>
            </li>
          }
        </ul>
      </div>
      <div class="msg-composer msg-composer--actions">
        <button kendoButton themeColor="primary" [disabled]="!picked.length" (click)="startConversation()">
          {{ 'startConversation' | translate }}
        </button>
      </div>
    }
  </aside>
}
